import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import { Plano, IntencaoInscricao, Inscricao, Cupom } from '../../models/planos/plano.model';

@Injectable({
  providedIn: 'root',
})
export class PlanosService implements ApiInterface {

  constructor(private apiService: ApiService) {}

  // --- Metodos padrao ApiInterface ---

  get<T>(url: string, data?: any): Observable<any> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<any>(path);
  }

  post<T>(url: string, data: any): Observable<any> {
    return this.apiService.create<any>(url, data);
  }

  put<T>(url: string, data: any): Observable<any> {
    return this.apiService.update<any>(url, data);
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  // --- Metodos especificos de Planos ---

  // Endpoint 1: GET /planos/listar
  listarPlanos(): Observable<Plano[]> {
    return this.apiService.read<Plano[]>('/planos/listar');
  }

  /**
   * GET /planos/especiais (master-only) — lista os planos do tipo ESPECIAL,
   * ordenados por nome. Usado no seletor de plano especial da aprovacao de
   * contador. Lista vazia e estado valido (nenhum plano especial cadastrado).
   */
  listarEspeciais(): Observable<Plano[]> {
    return this.apiService.read<Plano[]>('/planos/especiais');
  }

  // Endpoint 2: GET /tpago/criar-link-de-subscricao/{idPlano}?nomeCupom=&usuariosExtras=
  assinarPlano(idPlano: number, nomeCupom?: string, usuariosExtras?: number): Observable<IntencaoInscricao> {
    const url = `/tpago/criar-link-de-subscricao/${idPlano}${this.montarQuery(nomeCupom, usuariosExtras)}`;
    return this.apiService.read<IntencaoInscricao>(url);
  }

  // Endpoint 3: GET /QrVendas/geradorQRCode/{idPlano}?nomeCupom=&usuariosExtras=
  gerarQrCode(idPlano: number, nomeCupom?: string, usuariosExtras?: number): Observable<IntencaoInscricao> {
    const url = `/QrVendas/geradorQRCode/${idPlano}${this.montarQuery(nomeCupom, usuariosExtras)}`;
    return this.apiService.read<IntencaoInscricao>(url);
  }

  /**
   * Query string do checkout. `usuariosExtras` só vai quando informado: omitir faz o backend
   * manter os usuários adicionais que a empresa já tem, em vez de zerá-los na renovação.
   * Zero é valor legítimo (abrir mão deles), por isso o teste é por `undefined`/`null`.
   */
  private montarQuery(nomeCupom?: string, usuariosExtras?: number): string {
    const partes: string[] = [];
    if (nomeCupom) {
      partes.push(`nomeCupom=${encodeURIComponent(nomeCupom)}`);
    }
    if (usuariosExtras !== undefined && usuariosExtras !== null) {
      partes.push(`usuariosExtras=${usuariosExtras}`);
    }
    return partes.length ? `?${partes.join('&')}` : '';
  }

  // Endpoint 4: GET /inscricao/verificar-pagamento/{alias}
  verificarPagamento(alias: string): Observable<Inscricao> {
    return this.apiService.read<Inscricao>(`/inscricao/verificar-pagamento/${alias}`);
  }

  // Endpoint 5: PATCH /tpago/cancelar
  cancelarTransacaoTPago(alias: string): Observable<any> {
    return this.apiService.patch('/tpago/cancelar', { subscription_link_alias: alias });
  }

  // Endpoint 6: PATCH /inscricao/cancelar/{alias}
  cancelarAssinatura(alias: string): Observable<any> {
    return this.apiService.patch(`/inscricao/cancelar/${alias}`, {});
  }

  /**
   * Endpoint 7: GET /cupom-desconto-indicacao?nomeCupom=&planoId=
   *
   * `planoId` é o plano escolhido na vitrine. Vale a pena sempre enviá-lo: é assim que o
   * backend valida a faixa de um cupom especial e devolve as condições negociadas já
   * resolvidas — sem ele, um cupom da faixa errada só seria recusado na hora de pagar.
   */
  verificarCupom(nomeCupom: string, planoId?: number): Observable<Cupom> {
    const partes = [`nomeCupom=${encodeURIComponent(nomeCupom)}`];
    if (planoId !== undefined && planoId !== null) {
      partes.push(`planoId=${planoId}`);
    }
    return this.apiService.read<Cupom>(`/cupom-desconto-indicacao?${partes.join('&')}`);
  }

  /**
   * POST /inscricao/contratar-com-cupom — contratação com cupom especial de CORTESIA.
   *
   * Sem valor a cobrar não há pagamento a esperar: a assinatura volta já ativa, sem link nem
   * QR, então não há polling depois desta chamada.
   */
  contratarComCupom(idPlano: number, nomeCupom: string): Observable<Inscricao> {
    return this.apiService.create<Inscricao>('/inscricao/contratar-com-cupom', {
      planoId: idPlano,
      nomeCupom,
    });
  }

  // Endpoint 8: GET /inscricao/verificar-limite-de-venda?valorVenda=
  verificarLimiteVendas(valorVenda: number): Observable<any> {
    return this.apiService.read<any>(`/inscricao/verificar-limite-de-venda?valorVenda=${valorVenda}`);
  }
}
